import {
    Injector,
    Type,
} from '@angular/core';
import {
    ClientErrorCodes,
    ErrorTranslationKeys,
    IHostLicense,
    ILicense,
} from '@models';
import { Logger } from '@services/logger/abstract.logger';
import { TranslatableError } from '@utils/translatable-error';

import cloneDeep from 'lodash-es/cloneDeep';
import { CurrentUserAPI } from 'main/app-state/current-user/current-user.api';
import { UserAuthState } from 'main/app-state/current-user/current-user.types';
import { UserStateManager } from 'main/app-state/generics/user-state-manager';
import {
    BehaviorSubject,
    iif,
    Observable,
    throwError,
} from 'rxjs';
import {
    map,
    switchMap,
    tap,
} from 'rxjs/operators';

import { HostLicenseService } from './host-license.service';
import { LicenseAPI } from './license.api';
import { TenantLicenseService } from './tenant-license.service';

const emptyLicense = {
    id: 0,
    isRegistered: false,
    isExpired: true,
    features: {
        ANL: false,
    },
    attendedConcurrent: false,
    developmentConcurrent: false,
    studioXConcurrent: false,
    allowed: {
        unattended: 0,
        attended: 0,
        nonProduction: 0,
        development: 0,
        studioX: 0,
    },
    used: {
        unattended: 0,
        attended: 0,
        nonProduction: 0,
        development: 0,
        studioX: 0,
    },
} as IHostLicense;

export class LicenseManager extends UserStateManager<IHostLicense | ILicense> implements LicenseAPI {
    protected _state = new BehaviorSubject<IHostLicense | ILicense | null>(null);

    private _hostLicenseService: HostLicenseService;
    private _tenantLicenseService: TenantLicenseService;

    constructor(
        injector: Injector,
        private _user: CurrentUserAPI,
    ) {
        super(_user);

        this._hostLicenseService = injector.get<HostLicenseService>(HostLicenseService);
        this._tenantLicenseService = injector.get<TenantLicenseService>(TenantLicenseService);

        // The Injector doesn't work directly with abstract classes, so manual casting to Type<Logger> has to be done.
        // More details: https://github.com/angular/angular/issues/23611
        this._logStateChanges('License', injector.get<Logger>(Logger as Type<Logger>));
    }

    public fetch() {
        if (this._user.authState === UserAuthState.NotLoggedIn) {
            return this.clean();
        }

        const licenseSource = this._user.loggedInAsHost ?
            this._hostLicenseService.get({ top: 1 })
                .pipe(
                    map(license =>
                        !!license.data && !!license.data[0] ?
                            license.data[0] :
                            cloneDeep(emptyLicense),
                    ),
                ) :
            this._tenantLicenseService.get(
                undefined,
                '/odata/Settings/UiPath.Server.Configuration.OData.GetLicense',
            ) as unknown as Observable<ILicense>;

        return licenseSource
            .pipe(
                tap((license) => this._state.next(license)),
            );
    }

    public clean() {
        return new Observable<null>((subscriber) => {
            this._state.next(null);
            subscriber.next(null);
            subscriber.complete();
        });
    }

    public uploadLicense(file: File) {
        return iif(
            () => this._user.authState === UserAuthState.NotLoggedIn,

            throwError(new TranslatableError(
                ErrorTranslationKeys.SessionExpired,
                ClientErrorCodes.SessionExpired,
            )),

            iif(
                () => this._user.loggedInAsHost,
                this._hostLicenseService.uploadLicense(file),
                this._tenantLicenseService.uploadLicense(file),
            )
                .pipe(
                    switchMap(() => this.fetch()),
                ),
        );
    }

    public deleteLicense() {
        return iif(
            () => this._user.authState === UserAuthState.NotLoggedIn,

            throwError(new TranslatableError(
                ErrorTranslationKeys.SessionExpired,
                ClientErrorCodes.SessionExpired,
            )),

            iif(
                () => this._user.loggedInAsHost,
                this._hostLicenseService.delete((this.value as IHostLicense).id),
                this._tenantLicenseService.deleteLicense(),
            )
                .pipe(
                    switchMap(() => this.fetch()),
                ),
        );
    }

    public activateLicenseOnline(licenseKey: string, environment: string, email?: string) {
        return iif(
            () => this._user.authState === UserAuthState.NotLoggedIn,

            throwError(new TranslatableError(
                ErrorTranslationKeys.SessionExpired,
                ClientErrorCodes.SessionExpired,
            )),

            iif(
                () => this._user.loggedInAsHost,
                this._hostLicenseService.activateLicenseOnline(licenseKey, environment, email),
                this._tenantLicenseService.activateLicenseOnline(licenseKey, environment, email),
            )
                .pipe(
                    switchMap(() => this.fetch()),
                ),
        );
    }

    public activateLicenseOffline(file: File) {
        return iif(
            () => this._user.authState === UserAuthState.NotLoggedIn,

            throwError(new TranslatableError(
                ErrorTranslationKeys.SessionExpired,
                ClientErrorCodes.SessionExpired,
            )),

            iif(
                () => this._user.loggedInAsHost,
                this._hostLicenseService.activateLicenseOffline(file),
                this._tenantLicenseService.activateLicenseOffline(file),
            )
                .pipe(
                    switchMap(() => this.fetch()),
                ),
        );
    }

    public getLicenseOffline(licenseKey: string, environment: string, email?: string) {
        return iif(
            () => this._user.authState === UserAuthState.NotLoggedIn,

            throwError(new TranslatableError(
                ErrorTranslationKeys.SessionExpired,
                ClientErrorCodes.SessionExpired,
            )),

            iif(
                () => this._user.loggedInAsHost,
                this._hostLicenseService.getLicenseOffline(licenseKey, environment, email),
                this._tenantLicenseService.getLicenseOffline(licenseKey, environment, email),
            ),
        );
    }

    public updateLicenseOnline() {
        return iif(
            () => this._user.authState === UserAuthState.NotLoggedIn,

            throwError(new TranslatableError(
                ErrorTranslationKeys.SessionExpired,
                ClientErrorCodes.SessionExpired,
            )),

            iif(
                () => this._user.loggedInAsHost,
                this._hostLicenseService.updateLicenseOnline(),
                this._tenantLicenseService.updateLicenseOnline(),
            )
                .pipe(
                    switchMap(() => this.fetch()),
                ),
        );
    }

    public deactivateLicenseOnline(tenantId?: number) {
        return iif(
            () => this._user.authState === UserAuthState.NotLoggedIn,

            throwError(new TranslatableError(
                ErrorTranslationKeys.SessionExpired,
                ClientErrorCodes.SessionExpired,
            )),

            iif(
                () => this._user.loggedInAsHost,
                this._hostLicenseService.deactivateLicenseOnline(tenantId),
                this._tenantLicenseService.deactivateLicenseOnline(),
            ),
        );
    }

    public getDeactivateLicenseOffline(tenantId?: number) {
        return iif(
            () => this._user.authState === UserAuthState.NotLoggedIn,

            throwError(new TranslatableError(
                ErrorTranslationKeys.SessionExpired,
                ClientErrorCodes.SessionExpired,
            )),

            iif(
                () => this._user.loggedInAsHost,
                this._hostLicenseService.getDeactivateLicenseOffline(tenantId),
                this._tenantLicenseService.getDeactivateLicenseOffline(),
            ),
        );
    }
}
